import { useCallback, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Attendance } from "../domains/models/attendance/attendance";
import Locale from "../core/locale/locale";

dayjs.extend(utc);
dayjs.extend(timezone);

interface IState {
    records: Attendance[],
    isLoading: boolean,
    pageNumber: number,
    pageSize: number,
}

const useAttendanceData = (
    fetcher: (
        page: number,
        size: number,
        startDate?: string,
        endDate?: string,
        employeeId?: string,
        sortField?: string,
        sortDirection?: string,
        status?:string
    ) => Promise<any[]>
) => {

    const [attendanceState, setAttendanceState] = useState<IState>({
        records: [],
        isLoading: false,
        pageNumber: 0,
        pageSize: 10,
    });


    const fetchAttendanceData = useCallback(
        async (page?: number, size?: number, startDate?: string, endDate?: string, employeeId?: string, sortField?: string,
               sortDirection?: string,status?:string) => {
            const pageNum = page ?? attendanceState.pageNumber;
            const pageSize = size ?? attendanceState.pageSize;
            setAttendanceState(prev => ({...prev, isLoading: true}));


            try {
                let response: any;
                let mappedData: Attendance[] = [];

                response = await fetcher(
                    pageNum,
                    pageSize,
                    startDate,
                    endDate,
                    employeeId,
                    sortField,
                    sortDirection,
                    status
                );

                mappedData = response.map((item: any) => ({
                    ...item,
                    key: item.id || `${item.employeeId}-${item.date}`,
                    employeeName: item.employeeName,
                    checkInTime: item.checkInTime
                        ? dayjs
                            .utc(item.checkInTime, "HH:mm:ss.SSS")
                            .tz(dayjs.tz.guess())
                            .format(Locale.displayTimeShort)
                        : "-",
                    checkOutTime: item.checkOutTime
                        ? dayjs
                            .utc(item.checkOutTime, "HH:mm:ss.SSS")
                            .tz(dayjs.tz.guess())
                            .format(Locale.displayTimeShort)
                        : "-",
                    workingHours: item.workingHours || "-",
                }));

                setAttendanceState(prev => ({
                    ...prev,
                    records: mappedData
                }));


            } catch (error) {
                console.error("Error fetching admin-attendance-card:", error);
                setAttendanceState(prev => ({
                    ...prev,
                    records: [],
                }));
            } finally {
                setAttendanceState(prev => ({
                    ...prev,
                    isLoading: false
                }));
            }
        },
        [attendanceState.pageNumber,
            attendanceState.pageSize,
            fetcher]
    );


    return {
        attendanceList: attendanceState.records,
        isLoading: attendanceState.isLoading,
        fetchAttendanceData,
    };
};

export default useAttendanceData;
